/**
 * The simple slot pickers, one per captured popover.
 *
 * Every list here is monday's, in monday's order, with monday's caption. Where monday offers
 * something this product does not have (a Guest receiver, "+ Add a new column"), the entry is
 * **absent** rather than disabled (M1-B1 §3.2) — except "+ Add a new column", which is present
 * and routes to the board's own add-column gallery, because the capture shows the automations
 * centre creating columns.
 */
import { Avatar, Icon, Search, icons } from '@elizmonday/ui';
import { useMemo, useState } from 'react';
import { ANY_VALUE } from '@elizmonday/shared';
import { PopoverCaption, PopoverOption } from './SlotPopover';
import type { PickerColumnView, PickerDataView } from '../types';

export interface SlotEditorProps {
  value: unknown;
  onChange(next: unknown): void;
  close(): void;
  pickers: PickerDataView;
  /** The column this node already points at — what a label list is read from. */
  columnId: string | null;
  /** The block's `requiredColumnTypes`, narrowed further by the slot descriptor. */
  columnTypes?: readonly string[] | undefined;
  onAddColumn?: (() => void) | undefined;
}

/* ── columns ──────────────────────────────────────────────────────────────── */

const columnIdOf = (value: unknown): string | null => {
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null) {
    const id = (value as { columnId?: unknown }).columnId;
    return typeof id === 'string' ? id : null;
  }
  return null;
};

export function ColumnPicker({ value, onChange, pickers, columnTypes, onAddColumn }: SlotEditorProps) {
  const allowed = (column: PickerColumnView): boolean =>
    columnTypes === undefined || columnTypes.length === 0 || columnTypes.includes(column.type);
  const main = pickers.columns.filter(allowed);
  const sub = pickers.subitemColumns.filter(allowed);
  const selected = columnIdOf(value);

  return (
    <>
      <PopoverCaption>Select a column</PopoverCaption>
      {main.map((column) => (
        <PopoverOption
          key={column.id}
          selected={selected === column.id && !column.isSubitemColumn}
          testId={`slot-column-${column.id}`}
          onClick={() => onChange({ columnId: column.id, columnType: column.type })}
        >
          <Icon icon={icons.columnGeneric} size={16} ignoreFocusStyle />
          {column.title}
        </PopoverOption>
      ))}
      {onAddColumn === undefined ? null : (
        <button type="button" className="ez-automation-popover__add" onClick={onAddColumn} data-testid="slot-column-add">
          + Add a new column
        </button>
      )}
      {sub.length === 0 ? null : (
        <>
          <div className="ez-automation-popover__divider" role="separator" />
          <PopoverCaption>Select a subitem column</PopoverCaption>
          {sub.map((column) => (
            <PopoverOption
              key={`sub-${column.id}`}
              selected={selected === column.id}
              testId={`slot-subitem-column-${column.id}`}
              onClick={() => onChange({ columnId: column.id, columnType: column.type, isSubitemColumn: true })}
            >
              <Icon icon={icons.subitem} size={16} ignoreFocusStyle />
              {column.title}
            </PopoverOption>
          ))}
        </>
      )}
    </>
  );
}

/* ── status / dropdown labels ─────────────────────────────────────────────── */

export function labelsOfColumn(pickers: PickerDataView, columnId: string | null): PickerColumnView['labels'] {
  if (columnId === null) return [];
  const column = [...pickers.columns, ...pickers.subitemColumns].find((entry) => entry.id === columnId);
  return column?.labels ?? [];
}

/**
 * Capture `51-status-value-picker.png`: caption "Select value", **"Anything" first** (the
 * `$any$` wildcard), then the column's labels in the column's own order with their colours.
 */
export function LabelPicker({ value, onChange, pickers, columnId }: SlotEditorProps) {
  const labels = labelsOfColumn(pickers, columnId);
  const currentIndex = typeof value === 'object' && value !== null ? (value as { index?: number }).index : undefined;
  const isAny = typeof value === 'object' && value !== null && (value as Record<string, unknown>)[ANY_VALUE] === true;
  return (
    <>
      <PopoverCaption>Select value</PopoverCaption>
      <PopoverOption selected={isAny} testId="slot-label-any" onClick={() => onChange({ [ANY_VALUE]: true })}>
        <span className="ez-automation-dot ez-automation-dot--any" aria-hidden="true" />
        Anything
      </PopoverOption>
      {labels.map((label) => (
        <PopoverOption
          key={label.index}
          selected={currentIndex === label.index}
          testId={`slot-label-${String(label.index)}`}
          onClick={() => onChange({ index: label.index })}
        >
          <span className="ez-automation-dot" style={{ background: label.color ?? 'var(--ez-automation-dot)' }} aria-hidden="true" />
          {label.text}
        </PopoverOption>
      ))}
      {labels.length === 0 ? <div className="ez-automation-popover__empty">Choose a column first</div> : null}
    </>
  );
}

/** A dropdown condition takes a **set** of labels, so the popover stays open while you pick. */
export function LabelSetPicker({ value, onChange, pickers, columnId }: SlotEditorProps) {
  const labels = labelsOfColumn(pickers, columnId);
  const chosen = Array.isArray(value)
    ? value.map((entry) => (typeof entry === 'object' && entry !== null ? Number((entry as { index?: number }).index) : Number(entry)))
    : [];
  const toggle = (index: number): void => {
    const next = chosen.includes(index) ? chosen.filter((entry) => entry !== index) : [...chosen, index];
    onChange(next.map((entry) => ({ index: entry })));
  };
  return (
    <>
      <PopoverCaption>Select labels</PopoverCaption>
      {labels.map((label) => (
        <PopoverOption
          key={label.index}
          selected={chosen.includes(label.index)}
          testId={`slot-labels-${String(label.index)}`}
          onClick={() => toggle(label.index)}
        >
          <span className="ez-automation-dot" style={{ background: label.color ?? 'var(--ez-automation-dot)' }} aria-hidden="true" />
          {label.text}
        </PopoverOption>
      ))}
      {labels.length === 0 ? <div className="ez-automation-popover__empty">Choose a column first</div> : null}
    </>
  );
}

/* ── groups ───────────────────────────────────────────────────────────────── */

/** Capture `24-group-picker.png`: a search box, then "Top group (currently 'X')" and the groups. */
export function GroupPicker({ value, onChange, pickers }: SlotEditorProps) {
  const [query, setQuery] = useState('');
  const top = pickers.groups[0];
  const matches = pickers.groups.filter((group) => group.title.toLowerCase().includes(query.trim().toLowerCase()));
  return (
    <>
      <div className="ez-automation-popover__search">
        <Search
          value={query}
          onChange={setQuery}
          placeholder="Search group"
          size="small"
          data-testid="slot-group-search"
          aria-label="Search group"
        />
      </div>
      {top === undefined || query.trim().length > 0 ? null : (
        <PopoverOption selected={value === 'top'} testId="slot-group-top" onClick={() => onChange('top')}>
          <span className="ez-automation-dot ez-automation-dot--top" aria-hidden="true" />
          {`Top group (currently '${top.title}')`}
        </PopoverOption>
      )}
      {matches.map((group) => (
        <PopoverOption
          key={group.id}
          selected={value === group.id}
          testId={`slot-group-${group.id}`}
          onClick={() => onChange(group.id)}
        >
          <span className="ez-automation-dot" style={{ background: group.color }} aria-hidden="true" />
          {group.title}
        </PopoverOption>
      ))}
    </>
  );
}

/* ── people ───────────────────────────────────────────────────────────────── */

export function PersonPicker({
  value,
  onChange,
  pickers,
  // P-204: the receiver's Team-member branch renders this same picker, so its search box needs
  // an id of its own — `slot-person-search` is the slot's, `slot-receiver-person-search` the
  // receiver's copy. Both were `aria-label`-only, which the coverage ledger cannot count.
  searchTestId = 'slot-person-search',
}: SlotEditorProps & { searchTestId?: string }) {
  const [query, setQuery] = useState('');
  const chosen = Array.isArray(value) ? value.map(String) : value === undefined || value === null ? [] : [String(value)];
  const matches = pickers.people.filter((person) => person.name.toLowerCase().includes(query.trim().toLowerCase()));
  return (
    <>
      <div className="ez-automation-popover__search">
        <Search
          value={query}
          onChange={setQuery}
          placeholder="Search"
          size="small"
          data-testid={searchTestId}
          aria-label="Search people"
        />
      </div>
      {matches.map((person) => (
        <PopoverOption
          key={person.id}
          selected={chosen.includes(person.id)}
          testId={`slot-person-${person.id}`}
          onClick={() => onChange([Number(person.id)])}
        >
          <Avatar text={initials(person.name)} type="text" size="small" aria-label={person.name} />
          {person.name}
        </PopoverOption>
      ))}
    </>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

/* ── receiver ─────────────────────────────────────────────────────────────── */

type ReceiverBranch = null | 'user' | 'column' | 'team' | 'subscriber';

/**
 * Capture `36-receiver-picker.png`: caption "Who to notify?", then **Me · Team member ·
 * People (column) · Subscribers · Team**, each of the last four a submenu. *Guest* is monday's
 * sixth entry and is absent here: this product has no guest plan (M0-F8 §4).
 */
export function ReceiverPicker(props: SlotEditorProps) {
  const { value, onChange, pickers } = props;
  const [branch, setBranch] = useState<ReceiverBranch>(null);
  const current = (value ?? {}) as { type?: string; value?: unknown };
  const peopleColumns = pickers.columns.filter((column) => column.type === 'people' || column.type === 'person' || column.type === 'creation_log');

  if (branch === 'user') {
    return (
      <PersonPicker
        {...props}
        searchTestId="slot-receiver-person-search"
        value={current.type === 'user' ? current.value : null}
        onChange={(next) => {
          const ids = next as number[];
          onChange({ type: 'user', value: ids[0] ?? null });
        }}
      />
    );
  }
  if (branch === 'column') {
    return (
      <>
        <PopoverCaption>People (column)</PopoverCaption>
        {peopleColumns.map((column) => (
          <PopoverOption
            key={column.id}
            testId={`slot-receiver-column-${column.id}`}
            onClick={() => onChange({ type: 'column', value: column.id })}
          >
            <Icon icon={icons.columnPeople} size={16} ignoreFocusStyle />
            {column.title}
          </PopoverOption>
        ))}
        {peopleColumns.length === 0 ? <div className="ez-automation-popover__empty">This board has no people column</div> : null}
      </>
    );
  }
  if (branch === 'subscriber') {
    return (
      <>
        <PopoverCaption>Subscribers</PopoverCaption>
        <PopoverOption testId="slot-receiver-subscribers-item" onClick={() => onChange({ type: 'subscriber', value: 'item' })}>
          Item subscribers
        </PopoverOption>
        <PopoverOption testId="slot-receiver-subscribers-board" onClick={() => onChange({ type: 'subscriber', value: 'board' })}>
          Board subscribers
        </PopoverOption>
      </>
    );
  }
  if (branch === 'team') {
    // Teams are M0-F8's; until a team picker exists the branch says so rather than offering
    // a list that would resolve to nobody (`services-adapter.teamMembers` returns []).
    return (
      <>
        <PopoverCaption>Team</PopoverCaption>
        <div className="ez-automation-popover__empty">Teams arrive with M8.P2</div>
      </>
    );
  }

  return (
    <>
      <PopoverCaption>Who to notify?</PopoverCaption>
      <PopoverOption
        selected={current.type === 'me'}
        testId="slot-receiver-me"
        onClick={() => onChange({ type: 'me', value: null })}
      >
        <Avatar text={initials(pickers.me?.name ?? 'Me')} type="text" size="small" aria-label="Me" />
        Me
      </PopoverOption>
      <PopoverOption chevron testId="slot-receiver-user" onClick={() => setBranch('user')}>
        Team member
      </PopoverOption>
      <PopoverOption chevron testId="slot-receiver-column" onClick={() => setBranch('column')}>
        People (column)
      </PopoverOption>
      <PopoverOption chevron testId="slot-receiver-subscriber" onClick={() => setBranch('subscriber')}>
        Subscribers
      </PopoverOption>
      <PopoverOption chevron testId="slot-receiver-team" onClick={() => setBranch('team')}>
        Team
      </PopoverOption>
    </>
  );
}

/* ── small enumerations ───────────────────────────────────────────────────── */

export const CHOICES: Readonly<Record<string, ReadonlyArray<{ value: string; text: string }>>> = {
  is_condition: [
    { value: 'is', text: 'is' },
    { value: 'isnot', text: 'is not' },
  ],
  dropdown_condition: [
    { value: 'one', text: 'contains one of' },
    { value: 'only', text: 'contains only' },
    { value: 'all', text: 'contains all' },
  ],
  assign_behavior: [
    { value: 'replace', text: 'assign' },
    { value: 'add', text: 'add assignee' },
  ],
};

export function ChoicePicker({ value, onChange, kind }: SlotEditorProps & { kind: keyof typeof CHOICES }) {
  const options = CHOICES[kind] ?? [];
  return (
    <>
      <PopoverCaption>Select</PopoverCaption>
      {options.map((option) => (
        <PopoverOption
          key={option.value}
          selected={value === option.value}
          testId={`slot-choice-${option.value}`}
          onClick={() => onChange(option.value)}
        >
          {option.text}
        </PopoverOption>
      ))}
    </>
  );
}

/* ── duration ─────────────────────────────────────────────────────────────── */

export const DURATION_UNIT_OPTIONS = ['minutes', 'hours', 'days', 'businessDays', 'weeks', 'months'] as const;
export const DURATION_UNIT_LABEL: Readonly<Record<string, string>> = {
  minutes: 'minutes',
  hours: 'hours',
  days: 'days',
  businessDays: 'business days',
  weeks: 'weeks',
  months: 'months',
};

export function DurationPicker({ value, onChange, close }: SlotEditorProps) {
  const current = (value ?? {}) as Record<string, number | undefined>;
  const unit = DURATION_UNIT_OPTIONS.find((entry) => current[entry] !== undefined) ?? 'days';
  const count = current[unit] ?? 1;
  return (
    <div className="ez-automation-form">
      <PopoverCaption>How long?</PopoverCaption>
      <div className="ez-automation-form__row">
        <input
          type="number"
          min={1}
          className="ez-automation-input ez-automation-input--count"
          value={count}
          aria-label="Amount"
          data-testid="slot-duration-count"
          onChange={(event) => onChange({ [unit]: Math.max(1, Number(event.target.value) || 1) })}
        />
        <select
          className="ez-automation-input"
          value={unit}
          aria-label="Unit"
          data-testid="slot-duration-unit"
          onChange={(event) => onChange({ [event.target.value]: count })}
        >
          {DURATION_UNIT_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {DURATION_UNIT_LABEL[option]}
            </option>
          ))}
        </select>
      </div>
      <div className="ez-automation-form__actions">
        <button type="button" className="ez-automation-button" onClick={close} data-testid="slot-duration-done">
          Done
        </button>
      </div>
    </div>
  );
}

/* ── free text ────────────────────────────────────────────────────────────── */

export function TextSlotEditor({ value, onChange, close }: SlotEditorProps) {
  const [draft, setDraft] = useState(typeof value === 'string' ? value : '');
  return (
    <div className="ez-automation-form">
      <input
        className="ez-automation-input"
        value={draft}
        autoFocus
        aria-label="Value"
        data-testid="slot-text-input"
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          onChange(draft);
          close();
        }}
      />
      <div className="ez-automation-form__actions">
        <button type="button" className="ez-automation-button ez-automation-button--ghost" data-testid="slot-text-cancel" onClick={close}>
          Cancel
        </button>
        <button
          type="button"
          className="ez-automation-button"
          data-testid="slot-text-done"
          onClick={() => {
            onChange(draft);
            close();
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
}

/** `507`'s "create {an item}": the group it lands in plus its name. */
export function ItemValuesEditor({ value, onChange, close, pickers }: SlotEditorProps) {
  const current = (value ?? { columnValues: {}, groupId: null }) as { columnValues?: Record<string, unknown>; groupId?: string | null };
  const [name, setName] = useState(String(current.columnValues?.name ?? ''));
  const [groupId, setGroupId] = useState(current.groupId ?? pickers.groups[0]?.id ?? null);
  const options = useMemo(() => pickers.groups, [pickers.groups]);
  return (
    <div className="ez-automation-form">
      <PopoverCaption>The new item</PopoverCaption>
      <input
        className="ez-automation-input"
        value={name}
        placeholder="Item name"
        aria-label="Item name"
        data-testid="slot-item-name"
        onChange={(event) => setName(event.target.value)}
      />
      <select
        className="ez-automation-input"
        value={groupId ?? ''}
        aria-label="Group"
        data-testid="slot-item-group"
        onChange={(event) => setGroupId(event.target.value)}
      >
        {options.map((group) => (
          <option key={group.id} value={group.id}>
            {group.title}
          </option>
        ))}
      </select>
      <div className="ez-automation-form__actions">
        <button
          type="button"
          className="ez-automation-button"
          data-testid="slot-item-done"
          onClick={() => {
            onChange({ columnValues: { name }, groupId });
            close();
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
}
